import React from "react";
import { Search, X } from "lucide-react";
import { useDashboard } from "@/context/DashboardContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import RunbookCard from "@/components/RunbookCard";
import RecentExecutions from "@/components/RecentExecutions";
import { LoadingCards, ErrorState, EmptyState } from "@/components/states";
import { RUNBOOK } from "@/constants/testIds";

export default function Dashboard() {
  const { runbooks, loading, error, search, setSearch, query, hasFilters, clearFilters, reload, meta } = useDashboard();
  const { isContributor, startGoogleLogin } = useAuth();
  const navigate = useNavigate();

  const handleCreate = () => (isContributor ? navigate("/new") : startGoogleLogin("/new"));

  const renderContent = () => {
    if (loading) return <LoadingCards />;
    if (error) return <ErrorState message={error} onRetry={reload} />;
    if (runbooks.length === 0) {
      return (
        <EmptyState
          hasFilters={hasFilters}
          query={query}
          onReset={clearFilters}
          onCreate={handleCreate}
        />
      );
    }
    return (
      <div data-testid={RUNBOOK.grid} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {runbooks.map((rb) => (
          <RunbookCard key={rb.id} runbook={rb} />
        ))}
      </div>
    );
  };

  return (
    <div data-testid={RUNBOOK.dashboard} className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Runbook Library</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {meta ? `${meta.total} procedures` : "Loading procedures"} · find and execute the right one fast.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid={RUNBOOK.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search runbooks, tags, services…"
            className="pl-9 pr-9"
          />
          {search && (
            <button
              data-testid={RUNBOOK.clearSearch}
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {renderContent()}

      {!hasFilters && !loading && <RecentExecutions />}
    </div>
  );
}
